import React from 'react';
import {
  BrowserStreamPanel,
  Button,
  DocsAnnotation,
  Flex,
  StatusIndicator,
  Text,
  WaitCountdown,
  useCountdown,
} from '@chatsdet/ui';
import { ArrowLeft, Stop, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';
import { useBrowserStream } from '../../../../hooks/useBrowserStream';
import type { WorkflowRunWithStepsDto } from '../hooks/useWorkflowRun';
import { formatDuration, formatRelativeTime, runStatusLabel, statusVariant } from './run-formatters';
import { API_URL } from '../../../../config';
import { shouldShowLiveRunWaitCountdown } from './live-run-wait-state';
import { getPrestartStatus, type LiveRunStatusEvent } from './live-run-status';
import type { ExecutionEvent } from '../../../components/ExecutionMonitor';

type SmartWaitInfo = {
  nextCheckAt: string | null;
  deadlineAt: string | null;
  checkCount: number | null;
  lastVerdictSource: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Reads both the nested smartWait shape and the transitional waitState shape. */
export function readSmartWaitInfo(metadata: Record<string, unknown>): SmartWaitInfo | null {
  const nested = asRecord(metadata.smartWait) ?? asRecord(metadata.waitState);
  const source = nested ?? metadata;
  const nextCheckAt =
    typeof source.nextCheckAt === 'string'
      ? source.nextCheckAt
      : typeof source.resumeAt === 'string' && nested
        ? source.resumeAt
        : null;
  const deadlineAt =
    typeof source.deadlineAt === 'string'
      ? source.deadlineAt
      : typeof source.timeoutAt === 'string'
        ? source.timeoutAt
        : null;
  const checkCount = typeof source.checkCount === 'number' ? source.checkCount : null;
  const lastVerdictSource =
    typeof source.lastVerdictSource === 'string'
      ? source.lastVerdictSource
      : typeof source.lastSource === 'string'
        ? source.lastSource
        : null;

  if (!nested && !deadlineAt && checkCount === null && !lastVerdictSource) return null;
  return { nextCheckAt, deadlineAt, checkCount, lastVerdictSource };
}

function smartWaitSourceLabel(source: string | null): string {
  if (!source) return 'Not checked yet';
  if (source === 'cache' || source === 'cached') return 'Cached assertion';
  if (source === 'agent') return 'Agent';
  return source;
}

export function LiveRunPanel({
  runId,
  run,
  onBack,
  onShowDetails,
  live,
  statusEvent,
  builder,
}: {
  runId?: string | null;
  run: WorkflowRunWithStepsDto | null;
  onBack: () => void;
  onShowDetails: () => void;
  live: {
    isRunning: boolean;
    /** Stale-stream watchdog is resyncing + reopening the SSE — show a subtle hint. */
    isReconnecting?: boolean;
    currentStepId: string | null;
    /** Raw execution events. */
    events: ExecutionEvent[];
  };
  /** Latest nonterminal workflow status emitted while preparing/running. */
  statusEvent?: LiveRunStatusEvent | null;
  /** Builder mode - for AI-generated workflows */
  builder?: {
    streamId: string | null;
    workflowId: string;
    nodeCount: number;
    onCancel?: () => void;
  };
}) {
  const [isCancelling, setIsCancelling] = React.useState(false);


  // Determine which stream ID to use (builder takes precedence when active)
  const effectiveStreamId = builder?.streamId ?? runId ?? null;
  const isBuilderMode = Boolean(builder?.streamId);

  // Subscribe to browser stream for live screenshots, actions, and reasoning
  const { state: browserState } = useBrowserStream(effectiveStreamId);

  // Handle cancel builder
  const handleCancelBuilder = React.useCallback(async () => {
    if (!builder?.workflowId || isCancelling) return;
    setIsCancelling(true);
    try {
      const response = await fetch(`${API_URL}/workflows/${builder.workflowId}/builder/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        console.error('Failed to cancel builder');
      }
      builder.onCancel?.();
    } catch (err) {
      console.error('Failed to cancel builder:', err);
    } finally {
      setIsCancelling(false);
    }
  }, [builder, isCancelling]);

  // Derive status: prefer real-time SSE state during execution, fall back to query data
  const status: WorkflowRunWithStepsDto['status'] = (() => {
    // A run can be connected to its stream while still waiting in the queue.
    // Keep that state visible instead of letting the connection imply running.
    if (run?.status === 'queued') return 'queued';
    // Builder mode always shows as running while stream is active
    if (isBuilderMode) return 'running';
    // If SSE says we're running, trust that (real-time source)
    if (live.isRunning) return 'running';
    // If query has final status (not running), use it
    if (run?.status && run.status !== 'running') return run.status;
    // SSE done but query still shows running (stale) - show queued until refetch
    // Don't return stale 'running' status when SSE says we're done
    return 'queued';
  })();

  // Tick every second so the relative timestamp stays live
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (status !== 'running' && status !== 'queued') return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const durationMs =
    run?.startedAt && run?.finishedAt
      ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
      : null;

  // Find waiting step and extract countdown info (only for active runs)
  const waitingStepInfo = React.useMemo(() => {
    if (!run) return null;
    // Only show countdown for active runs (not canceled, failed, or success)
    if (
      run.status === 'canceled' ||
      run.status === 'failed' ||
      run.status === 'inconclusive' ||
      run.status === 'success'
    ) {
      return null;
    }

    // Only treat as waiting if the latest step is itself waiting — a stale
    // 'waiting' status on an earlier step must not block the video/screenshots.
    const lastStep = run.steps[run.steps.length - 1];
    if (!lastStep || lastStep.status !== 'waiting') return null;
    const waitingStep = lastStep;

    // Check step-level metadata first
    const stepMetadata = (waitingStep.metadataJson ?? {}) as Record<string, unknown>;
    let resumeAt = typeof stepMetadata.resumeAt === 'string' ? stepMetadata.resumeAt : null;
    let delaySeconds =
      typeof stepMetadata.delaySeconds === 'number' ? stepMetadata.delaySeconds : undefined;

    // Fallback to run-level metadata
    if (!resumeAt && run.metadataJson) {
      const runMetadata = run.metadataJson as Record<string, unknown>;
      if (typeof runMetadata.resumeAt === 'string') {
        resumeAt = runMetadata.resumeAt;
      }
      if (delaySeconds === undefined && typeof runMetadata.delaySeconds === 'number') {
        delaySeconds = runMetadata.delaySeconds;
      }
    }

    const smartWait = readSmartWaitInfo(stepMetadata);
    const runSmartWait = run.metadataJson ? readSmartWaitInfo(run.metadataJson) : null;
    const smartWaitInfo = smartWait ?? runSmartWait;
    if (!resumeAt && smartWaitInfo?.nextCheckAt) resumeAt = smartWaitInfo.nextCheckAt;
    if (!resumeAt) return null;
    return { resumeAt, delaySeconds, smartWait: smartWaitInfo };
  }, [run]);

  // Countdown hook for waiting state
  const countdown = useCountdown(waitingStepInfo?.resumeAt, waitingStepInfo?.delaySeconds);
  const showWaitCountdown = shouldShowLiveRunWaitCountdown({
    hasWaitingStep: Boolean(waitingStepInfo),
    isLiveRunning: live.isRunning,
    countdownComplete: countdown.isComplete,
    countdownResuming: countdown.isResuming,
  });

  const hasBrowserActivity = Boolean(
    browserState.latestScreenshot ||
    browserState.latestAction ||
    browserState.latestThought ||
    browserState.actions.length > 0 ||
    browserState.reasoning.length > 0
  );
  const prestart = getPrestartStatus({
    status,
    events: live.events,
    statusEvent: statusEvent ?? null,
    sandbox: run?.sandboxV2Sandbox ?? null,
  });
  const emptyStateMessage = !hasBrowserActivity ? prestart.message : undefined;
  const headerPrestartLabel =
    !isBuilderMode && !hasBrowserActivity && (status === 'queued' || status === 'running')
      ? prestart.label
      : null;

  // Meta-row items, in display order — collected so `•` separators only ever
  // appear between two items that actually rendered (each source is
  // independently conditional: reconnect hint, builder node count, duration,
  // assertion counter).
  const metaItems: { key: string; node: React.ReactNode }[] = [];
  if (!isBuilderMode && live.isReconnecting && status === 'running') {
    metaItems.push({
      key: 'reconnecting',
      node: (
        <Flex align="center" gap="xs" aria-live="polite" className="min-w-0 shrink">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-pulse shrink-0" />
          <Text size="xs" className="min-w-0 truncate text-muted-foreground">
            Syncing live updates…
          </Text>
        </Flex>
      ),
    });
  }
  if (isBuilderMode && builder?.nodeCount !== undefined && builder.nodeCount > 0) {
    metaItems.push({
      key: 'node-count',
      node: (
        <Text size="xs" className="shrink-0 whitespace-nowrap text-muted-foreground">
          {builder.nodeCount} node{builder.nodeCount !== 1 ? 's' : ''} created
        </Text>
      ),
    });
  }
  if (!isBuilderMode && durationMs !== null) {
    metaItems.push({
      key: 'duration',
      node: (
        <Text size="xs" className="shrink-0 whitespace-nowrap text-muted-foreground">
          {formatDuration(durationMs)}
        </Text>
      ),
    });
  }
  if (!isBuilderMode) {
    metaItems.push({
      key: 'relative-time',
      node: (
        <Text size="xs" className="shrink-0 whitespace-nowrap text-muted-foreground">
          {run ? formatRelativeTime(run.createdAt) : '—'}
        </Text>
      ),
    });
  }
  if (browserState.assertionSummary && browserState.assertionSummary.total > 0) {
    metaItems.push({
      key: 'assertions',
      node: (
        <Flex align="center" gap="xs" className="shrink-0 whitespace-nowrap">
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={10} weight="fill" /> {browserState.assertionSummary.passed}
          </span>
          {browserState.assertionSummary.warnings > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
              <Warning size={10} weight="fill" /> {browserState.assertionSummary.warnings}
            </span>
          )}
          {browserState.assertionSummary.failures > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
              <XCircle size={10} weight="fill" /> {browserState.assertionSummary.failures}
            </span>
          )}
          <Text size="xs" className="text-muted-foreground">
            / {browserState.assertionSummary.total}
          </Text>
        </Flex>
      ),
    });
  }

  return (
    <DocsAnnotation slug="reference/runs" title="Live Execution">
      <div className="flex flex-col h-full">
        {/* Two-row header — mirrors `RunDetailsPanel`. `px-4` lives here (and
            on the scroll body below) rather than on the sidebar container,
            which is full-bleed so the run history list's rows can run edge
            to edge. A single row can't fit back button + status + reconnect
            hint + duration + time + assertion counter at the sidebar's
            320px floor without wrapping mid-phrase, so status lives on row 1
            and everything else moves to a wrapping meta row below it. */}
        <div className="px-4 py-3 border-b border-border space-y-1.5">
          <Flex justify="between" align="center" gap="sm">
            <Flex align="center" gap="sm">
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Back to Runs"
                aria-label="Back to Runs"
                onPress={onBack}
                className="shrink-0"
              >
                <ArrowLeft size={16} />
              </Button>

              <StatusIndicator variant={statusVariant[status]} size="sm" pulse={status === 'running'}>
                {isBuilderMode
                  ? 'Generating'
                  : headerPrestartLabel
                    ? `${runStatusLabel(status)} · ${headerPrestartLabel}`
                    : runStatusLabel(status)}
              </StatusIndicator>
            </Flex>
            {isBuilderMode ? (
              <Button
                variant="outline"
                size="sm"
                iconLeft={<Stop size={16} weight="fill" />}
                onPress={handleCancelBuilder}
                isDisabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel'}
              </Button>
            ) : !live.isRunning ? (
              <Button variant="ghost" size="sm" onPress={onShowDetails}>
                Details
              </Button>
            ) : null}
          </Flex>

          {/* No `pl-8` indent: the meta row starts on the header's own
              gutter, lining up under the back button above it, same rule as
              `RunDetailsPanel`. `flex-wrap` lets the assertion counter drop
              to its own line at 320px instead of squeezing its neighbours. */}
          {metaItems.length > 0 && (
            <Flex align="center" gap="sm" className="flex-wrap">
              {metaItems.map((item, index) => (
                <React.Fragment key={item.key}>
                  {index > 0 && (
                    <Text size="xs" className="shrink-0 text-muted-foreground">
                      •
                    </Text>
                  )}
                  {item.node}
                </React.Fragment>
              ))}
            </Flex>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {showWaitCountdown ? (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 p-6">
              <div className="flex flex-col items-center">
                <WaitCountdown
                  formatted={countdown.formatted}
                  progress={countdown.progress}
                  isComplete={countdown.isComplete}
                  isResuming={countdown.isResuming}
                  size="lg"
                />
                <Text size="sm" color="muted" className="mt-4 text-center">
                  {waitingStepInfo?.smartWait
                    ? 'The pass condition will be checked again automatically'
                    : 'Workflow will resume automatically'}
                </Text>
                {waitingStepInfo?.smartWait && (
                  <div className="mt-4 w-full max-w-sm rounded-md border border-amber-200/80 bg-white/50 px-3 py-2 dark:border-amber-800/80 dark:bg-black/10">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <Text size="xs" className="text-muted-foreground">
                        Checks completed
                      </Text>
                      <Text size="xs" className="text-right tabular-nums">
                        {waitingStepInfo.smartWait.checkCount ?? '—'}
                      </Text>
                      <Text size="xs" className="text-muted-foreground">
                        Last check
                      </Text>
                      <Text size="xs" className="text-right">
                        {smartWaitSourceLabel(waitingStepInfo.smartWait.lastVerdictSource)}
                      </Text>
                      {waitingStepInfo.smartWait.deadlineAt && (
                        <>
                          <Text size="xs" className="text-muted-foreground">
                            Maximum timeout
                          </Text>
                          <Text size="xs" className="text-right">
                            {formatRelativeTime(waitingStepInfo.smartWait.deadlineAt)}
                          </Text>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <BrowserStreamPanel
              state={browserState}
              layout="stacked"
              screenshotAspectRatio="aspect-video"
              maxActions={10}
              showReasoningPanel={true}
              showActionHistory={false}
              emptyScreenshotLabel={
                isBuilderMode
                  ? 'Preparing the builder…'
                  : live.isRunning || status === 'queued'
                    ? (emptyStateMessage ?? 'Preparing the live run…')
                    : 'No screenshots yet'
              }
              emptyReasoningLabel={
                isBuilderMode
                  ? 'Builder is exploring the app...'
                  : live.isRunning || status === 'queued'
                    ? (emptyStateMessage ?? 'Preparing the live run…')
                    : 'Agent will appear here'
              }
            />
          )}
        </div>
      </div>
    </DocsAnnotation>
  );
}
